"use client";

import React, { useState } from "react";
import { Button } from "../../button.js";
import { Users } from "lucide-react";
import { AttendeeItem } from "./AttendeeItem.js";
import { EmptyState } from "../shared/EmptyState.js";
import { SummaryStats } from "../shared/SummaryStats.js";
import { useToast } from "../../../hooks/use-toast.js";

interface Attendee {
  email: string;
  name?: string;
  responseStatus?: string;
  isOrganizer?: boolean;
}

interface AttendeeListProps {
  attendees: Attendee[];
  organizer?: Attendee;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  maxVisible?: number;
  showSummary?: boolean;
  variant?: "compact" | "detailed";
  className?: string;
}

export function AttendeeList({
  attendees,
  organizer,
  onComposeEmail,
  maxVisible = 5,
  showSummary = true,
  variant = "detailed",
  className = ""
}: AttendeeListProps) {
  const { toast } = useToast();
  const [showAll, setShowAll] = useState(false);

  // Combine organizer and attendees, ensuring organizer is first
  const allAttendees = organizer 
    ? [organizer, ...attendees.filter(a => a.email !== organizer.email)]
    : attendees;

  const visibleAttendees = showAll ? allAttendees : allAttendees.slice(0, maxVisible);
  const hasMore = allAttendees.length > maxVisible;

  const handleCopyAllEmails = async () => {
    const allEmails = allAttendees.map(a => a.email).join(", ");
    try {
      await navigator.clipboard.writeText(allEmails);
      toast({
        title: "All emails copied",
        description: "All attendee emails copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy emails to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleEmailAll = () => {
    const allEmails = allAttendees.map(a => a.email);
    if (onComposeEmail) {
      onComposeEmail(allEmails);
    } else {
      const mailtoLink = `mailto:${allEmails.join(',')}`;
      window.open(mailtoLink, '_blank');
      toast({
        title: "Email opened",
        description: `Opening email to ${allEmails.length} recipients`,
      });
    }
  };

  if (allAttendees.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No attendees found"
        description="No attendees found for this event."
        className={className}
      />
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary */}
      {showSummary && (
        <SummaryStats
          attendees={allAttendees}
          showActions={true}
          onCopyAll={handleCopyAllEmails}
          onEmailAll={handleEmailAll}
        />
      )}

      {/* Attendees List */}
      <div className="space-y-2">
        {visibleAttendees.map((attendee, index) => (
          <AttendeeItem
            key={`${attendee.email}-${index}`}
            email={attendee.email}
            name={attendee.name}
            responseStatus={attendee.responseStatus}
            isOrganizer={attendee.isOrganizer}
            onComposeEmail={onComposeEmail}
            variant={variant}
          />
        ))}
        
        {hasMore && !showAll && (
          <div className="text-center py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(true)}
              className="text-muted-foreground"
            >
              View {allAttendees.length - maxVisible} more attendees
            </Button>
          </div>
        )}
        
        {showAll && hasMore && (
          <div className="text-center py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(false)}
              className="text-muted-foreground"
            >
              Show less
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
